// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { ValidatingFormComponent } from '@/hybrid/formbuilder/formBuilder.module';
import { useTranslation } from 'react-i18next';
import { Field, useForm } from 'react-final-form';
import { InputGroup, Modal } from 'react-bootstrap';
import { getFormFieldProps, getValidationFunction } from '@/hybrid/formbuilder/formbuilder.utilities';
import { FormFieldWrapper } from '@/hybrid/formbuilder/FormFieldWrapper';
import { useShowError } from '@/hybrid/formbuilder/hooks/useShowError';
import { SelectAssetModal } from '@/hybrid/core/SelectAssetModal.molecule';
import { TextButton } from '@/hybrid/core/TextButton.atom';
import { Icon } from '@/hybrid/core/Icon.atom';
import { itemIconClass } from '@/hybrid/utilities/utilities';
import { getAssetFromAncestors } from '@/hybrid/utilities/httpHelpers.utilities';

export interface SelectAssetIF extends ValidatingFormComponent<any> {
  component: 'SelectAssetFormComponent';
  iconPartialTooltipKey: string;
  scopeIds?: string[];
  excludeGloballyScoped?: boolean;
  helpTextKey?: string;
  customErrorText?: string;
}

export const SelectAssetFormComponent: React.FunctionComponent<SelectAssetIF> = (props) => {
  const {
    name,
    value,
    testId = 'selectAsset',
    validation,
    extendValidation,
    onChange,
    iconPartialTooltipKey,
    helpTextKey = undefined,
    customErrorText,
    scopeIds = undefined,
    excludeGloballyScoped = false,
  } = props;

  const { t } = useTranslation();

  const formState = useForm().getState();
  const { showError, setShowError } = useShowError(false);
  const [isSelectingAsset, setIsSelectingAsset] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(value);
  useEffect(() => setSelectedAsset(value), [value]);

  const defaultValidation = (value: any) => !value;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const validateAsset = (item) => Promise.resolve(!appliedValidation(item));
  const [canSave, setCanSave] = useState(false);
  useEffect(() => {
    validateAsset(selectedAsset).then((result) => {
      setCanSave(result);
    });
  }, [selectedAsset, isSelectingAsset]);

  const clearAndClose = () => {
    setIsSelectingAsset(false);
  };

  const onSave = () => {
    onChange(selectedAsset);
    setIsSelectingAsset(false);
  };

  const searchResult = (item) => (
    <div className="flexColumnContainer flexSpaceBetween flexFill ptb2">
      {(item && (
        <>
          <div className="flexColumnContainer flexCenter">
            <Icon
              icon={itemIconClass(item)}
              testId={`${testId}-icon`}
              type="inherit"
              extraClassNames="sq-fairly-dark-gray pl5 pr10"
              large={true}
            />
          </div>
          <div className="flexFill" data-testid={`${testId}-selected`}>
            <div className="searchResultName">
              <span className="simple-word-break">{item.name}</span>
            </div>
            <div className="xsmall pb2 sq-fairly-dark-gray text-italic simple-word-break">
              {(item?.ancestors && getAssetFromAncestors(item.ancestors).formattedName) || item?.datasource?.name}
            </div>
          </div>
        </>
      )) || (
        <div className="sq-fairly-dark-gray text-italic" data-testid={`${testId}-notSelected`}>
          <span>{t('FORM.NO_ASSET_SELECTED')}</span>
        </div>
      )}
    </div>
  );

  const title = <Modal.Title>{t('FORM.SELECT_ASSET')}</Modal.Title>;

  const body = helpTextKey ? (
    <div className="alert alert-info" data-testid={`${testId}-helpText`}>
      {t(helpTextKey)}
    </div>
  ) : (
    <></>
  );

  const footer = (
    <Modal.Footer>
      <div className="flexColumnContainer flexJustifyEnd pt2">
        <div>
          <TextButton onClick={clearAndClose} label={t('CANCEL')} extraClassNames="mr5" testId={`${testId}-cancel`} />
          <TextButton
            extraClassNames={canSave ? '' : 'noMouse'}
            onClick={onSave}
            label={t('SAVE')}
            variant="theme"
            disabled={!canSave}
            testId={`${testId}-save`}
          />
        </div>
      </div>
    </Modal.Footer>
  );

  return (
    <>
      <FormFieldWrapper
        wrapperClassNames="flexFill"
        testId={testId}
        showError={showError}
        customErrorText={customErrorText}>
        <Field name={name} validate={appliedValidation}>
          {({ input, meta }) => {
            const formFieldProps = getFormFieldProps(formState, input, meta, props);
            setShowError(formFieldProps.showError);
            return (
              <InputGroup>
                <div
                  className={classNames(
                    'width-maximum form-control ptb0 flexColumnContainer flexAlignCenter',
                    formFieldProps.extraClassNames,
                  )}
                  data-testid={`${testId}-value`}>
                  {searchResult(formFieldProps.value)}
                </div>
                <InputGroup.Append>
                  <TextButton
                    icon="fa-pencil"
                    iconStyle="theme"
                    testId={`${testId}-edit`}
                    onClick={() => {
                      setIsSelectingAsset(true);
                    }}
                  />
                </InputGroup.Append>
              </InputGroup>
            );
          }}
        </Field>
      </FormFieldWrapper>
      {isSelectingAsset && (
        <SelectAssetModal
          header={title}
          body={body}
          footer={footer}
          onClose={clearAndClose}
          assetId={selectedAsset?.id}
          iconPartialTooltipKey={iconPartialTooltipKey}
          onSelect={setSelectedAsset}
          validateAsset={validateAsset}
          scopeIds={scopeIds}
          excludeGloballyScoped={excludeGloballyScoped}
          testId={`${testId}-modal`}
        />
      )}
    </>
  );
};
